<script setup lang="ts">
import Loading from '@legacy/components/common/Loading.vue';
import NoticeBox from '@legacy/components/common/NoticeBox.vue';
import axios from 'axios';
import BackupCodesModal from 'frontend/components/domain/two-factor-auth/BackupCodesModal.vue';
import SetupModal from 'frontend/components/domain/two-factor-auth/SetupModal.vue';
import { computed, ref } from 'vue';
import { SphDialog } from '@/components/base/dialog';
import { SphField } from '@/components/base/field';
import { SphSwitch } from '@/components/base/switch';
import { useI18n } from '@/composables/i18n';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps({
    loginContextId: {
        type: Number,
        required: true,
    },
});

const emit = defineEmits<{
    setupComplete: []
}>();

interface TwoFactorStatus {
    id: number
    enabled: boolean
    twoFactorSetupRequired: boolean
    backupCodesRemaining: number
    setup?: {
        qrCode: string
        secret: string
    }
}
const { i18n } = useI18n();
const status = ref<TwoFactorStatus | null>(null);
const loading = ref(true);
const showSetupModal = ref(false);
const showBackupCodesModal = ref(false);
const backupCodes = ref<string[]>([]);
const error = ref<string | null>(null);
const disabling = ref(false);
const generatingBackupCodes = ref(false);
const showConfirmDelete = ref(false);
const showBackupCodeConfirmRequired = computed(() => status.value?.backupCodesRemaining && status.value.backupCodesRemaining > 0);
const showBackupCodeConfirm = ref(false);

async function load() {
    loading.value = true;
    error.value = null;

    try {
        const response = await axios.get(`/2fa/${props.loginContextId}`);
        status.value = response.data.data;
    } catch (e: any) {
        error.value = e.response?.data?.message || i18n.t('Laden fehlgeschlagen');
        status.value = null;
    } finally {
        loading.value = false;
    }
}

function startSetup() {
    showSetupModal.value = true;
}

function onSetupComplete() {
    showSetupModal.value = false;
    load();
    generateBackupCodes();
    emit('setupComplete');
}

function onSetupCancel() {
    showSetupModal.value = false;
}

async function disable(event: any) {
    disabling.value = true;
    error.value = null;

    try {
        await axios.delete(`/2fa/${props.loginContextId}`);
        await load();
        event.done(true);
    } catch (e: any) {
        error.value = e.response?.data?.message || i18n.t('Deaktivieren fehlgeschlagen');
        event.done(false);
    } finally {
        disabling.value = false;
    }
}

async function generateBackupCodes(event?: any) {
    generatingBackupCodes.value = true;
    error.value = null;

    try {
        const response = await axios.post(`/2fa/${props.loginContextId}/backup-codes`);
        backupCodes.value = response.data.data.codes;
        await load();
        if (event) {
            event.done(true);
            // Open modal in next tick after confirm dialog is closed
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
        showBackupCodesModal.value = true;
    } catch (e: any) {
        error.value = e.response?.data?.message || i18n.t('Backup-Codes konnten nicht generiert werden');
        if (event) {
            event.done(false);
        }
    } finally {
        generatingBackupCodes.value = false;
    }
}

function onBackupCodesModalClose() {
    showBackupCodesModal.value = false;
    backupCodes.value = [];
}

function handleState() {
    if (status.value!.enabled === true) {
        showConfirmDelete.value = true;
    } else {
        startSetup();
    }
}

load();
</script>

<template>
    <div>
        <NoticeBox
            variant="warning"
        >
            <template #headline>
                {{ $t('Hinweis') }}
            </template>
            <template #body>
                {{ $t('Wird die Zwei-Faktor Authentifizierung aktiviert, muss zur Nutzung der App die aktuellste Version installiert werden, alte App-Versionen werden nicht unterstützt.') }}
                <template v-if="status?.twoFactorSetupRequired">
                    <br /><br />{{ $t('Die Einrichtung hat die Zwei-Faktor-Authentifizierung verpflichtend aktiviert, diese muss zunächst eingerichtet werden um den Account nutzen zu können.') }}
                </template>
            </template>
        </NoticeBox>

        <SphDialog
            variant="form"
            :state="showBackupCodeConfirm"
            :title="$t('Backup-Codes')"
            :submit-button-label="$t('Generieren')"
            @submit="generateBackupCodes"
            @close="showBackupCodeConfirm = false"
        >
            {{ $t('Es sind noch Backup-Codes übrig. Mit einer Neugenerierung werden die alten Codes ungültig. Fortfahren?') }}
        </SphDialog>

        <SphDialog
            variant="form"
            :state="showConfirmDelete"
            :title="$t('Zwei-Faktor-Authentifizierung deaktivieren')"
            :submit-button-label="$t('Deaktivieren')"
            @submit="disable"
            @close="showConfirmDelete = false"
        >
            {{ $t('Nach der Deaktivierung muss die Zwei-Faktor-Authentifizierung gegebenenfalls erneut eingerichtet werden.') }}
        </SphDialog>

        <Loading v-if="loading" />

        <div
            v-else-if="error"
            class="kgr-inputGroup__text kgr-inputGroup__text--error"
        >
            {{ error }}
        </div>

        <div v-else-if="status">
            <SphField
                :label="$t('2FA aktivieren/deaktivieren')"
                label-for="status"
                inline
                class="my-6"
            >
                <SphSwitch
                    id="status"
                    :model-value="status.enabled"
                    :disabled="disabling"
                    @update:model-value="handleState"
                />
            </SphField>
            <div
                v-if="status?.enabled"
            >
                <p class="kgr-text">
                    {{ $t('Die Zwei-Faktor-Authentifizierung ist für dieses Konto aktiviert.') }}<br />
                    {{ $t('Bei jeder Anmeldung wird zusätzlich ein Code aus der Authenticator-App abgefragt.') }}
                </p>

                <hr class="kgr-separator kgr-separator--light" />

                <div class="backup-codes-section">
                    <h2 class="kgr-headline kgr-headline--h3">
                        {{ $t('Backup-Codes') }}
                    </h2>
                    <p class="kgr-text">
                        {{ $t('Backup-Codes können verwendet werden, wenn kein Zugriff mehr auf die Authenticator-App besteht.') }}
                    </p>
                    <p class="kgr-text kgr-text--bold my-4">
                        {{ $t('Verbleibende Codes') }}: {{ status.backupCodesRemaining }}
                    </p>
                    <button
                        type="button"
                        class="kgr-button kgr-button--secondary"
                        :disabled="generatingBackupCodes"
                        @click="showBackupCodeConfirmRequired ? showBackupCodeConfirm = true : generateBackupCodes()"
                    >
                        {{ generatingBackupCodes ? $t('Wird generiert...') : $t('Neue Backup-Codes generieren') }}
                    </button>
                </div>

                <hr class="kgr-separator kgr-separator--light" />
            </div>

            <div v-else>
                <p class="kgr-text">
                    {{ $t('2FA schützt diesen Account mit einer zusätzlichen Sicherheitsebene.') }}
                </p>
                <br />
                <NoticeBox>
                    <template #body>
                        {{ $t('Es ist eine Authenticator-App aus dem Google Play Store oder dem iOS App Store erforderlich (z.B. Google Authenticator, Authy, Microsoft Authenticator).') }}
                    </template>
                </NoticeBox>
            </div>
        </div>

        <SetupModal
            v-if="showSetupModal"
            :login-context-id="props.loginContextId"
            @complete="onSetupComplete"
            @cancel="onSetupCancel"
        />

        <BackupCodesModal
            v-if="showBackupCodesModal"
            :codes="backupCodes"
            @close="onBackupCodesModalClose"
        />
    </div>
</template>

<style scoped lang="scss">

</style>
